import {
  ShareAccessResponseSchema,
  ShareAuditListResponseSchema,
  ShareDownloadResponseSchema,
  ShareLinkCreateResponseSchema,
  ShareLinkDeleteResponseSchema,
  ShareLinkListResponseSchema,
  ShareLinkRevokeResponseSchema,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "./client.js";

import type {
  ShareAccessResponse,
  ShareAuditListResponse,
  ShareDownloadResponse,
  ShareLink,
  ShareLinkCreate,
  ShareLinkCreateResponse,
  ShareLinkListResponse,
} from "@l30/shared";

export interface ShareCredentials {
  email?: string;
  password?: string;
}

const SHARE_LINKS_KEY = ["share-links"] as const;

export function fetchShare(token: string, credentials: ShareCredentials = {}) {
  const params = shareParams(credentials);
  const qs = params.toString();
  return apiFetch<ShareAccessResponse>(
    `/share/${encodeURIComponent(token)}${qs ? `?${qs}` : ""}`,
    {},
    ShareAccessResponseSchema,
  );
}

export function fetchShareDownload(token: string, credentials: ShareCredentials = {}) {
  const params = shareParams(credentials);
  const qs = params.toString();
  return apiFetch<ShareDownloadResponse>(
    `/share/${encodeURIComponent(token)}/download${qs ? `?${qs}` : ""}`,
    {},
    ShareDownloadResponseSchema,
  );
}

function shareParams(credentials: ShareCredentials) {
  const params = new URLSearchParams();
  if (credentials.email?.trim()) params.set("email", credentials.email.trim());
  if (credentials.password) params.set("password", credentials.password);
  return params;
}

export function useShareLinks(documentId?: string, enabled = true) {
  return useQuery({
    queryKey: [...SHARE_LINKS_KEY, documentId ?? "all"],
    queryFn: () => {
      const params = new URLSearchParams();
      if (documentId) params.set("documentId", documentId);
      const qs = params.toString();
      return apiFetch<ShareLinkListResponse>(
        `/sharing${qs ? `?${qs}` : ""}`,
        {},
        ShareLinkListResponseSchema,
      );
    },
    enabled,
  });
}

export function useCreateShareLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ShareLinkCreate) =>
      apiFetch<ShareLinkCreateResponse>(
        "/sharing",
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        ShareLinkCreateResponseSchema,
      ),
    onSuccess: (share) => {
      qc.invalidateQueries({ queryKey: SHARE_LINKS_KEY });
      qc.invalidateQueries({ queryKey: [...SHARE_LINKS_KEY, share.documentId] });
    },
  });
}

export function useRevokeShareLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (share: ShareLink) =>
      apiFetch(`/sharing/${share.id}/revoke`, { method: "POST" }, ShareLinkRevokeResponseSchema),
    onSuccess: (_result, share) => {
      qc.invalidateQueries({ queryKey: SHARE_LINKS_KEY });
      qc.invalidateQueries({ queryKey: [...SHARE_LINKS_KEY, share.documentId] });
    },
  });
}

export function useDeleteShareLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (share: ShareLink) =>
      apiFetch(`/sharing/${share.id}`, { method: "DELETE" }, ShareLinkDeleteResponseSchema),
    onSuccess: (_result, share) => {
      qc.invalidateQueries({ queryKey: SHARE_LINKS_KEY });
      qc.invalidateQueries({ queryKey: [...SHARE_LINKS_KEY, share.documentId] });
    },
  });
}

export function useShareAudit(shareId?: string, enabled = true) {
  return useQuery({
    queryKey: [...SHARE_LINKS_KEY, shareId ?? "none", "audit"],
    queryFn: () =>
      apiFetch<ShareAuditListResponse>(
        `/sharing/${shareId}/audit`,
        {},
        ShareAuditListResponseSchema,
      ),
    enabled: enabled && !!shareId,
  });
}
